/* eslint-disable complexity */
import React, { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { TypeaheadOption } from './option/option';
import { TypeaheadProvider } from './typeahead-context';
import { DefaultOptionTemplate } from '../templates/default-template';
import { MotifIconButton } from '../../button/icon-button/icon-button';
import { ReactComponent as HardwareIcClose24px } from '../../../assets/icons/navigation/ic_close_24px.svg';
import { FormFieldContext } from '../form-field/form-field.context';
import { canHandleOutside } from '../../../utils/click-outside.util';

let nextUniqueId;
nextUniqueId = 0;

const REM = 14;
const OPTION_HEIGHT = 44 / REM;

export const returnDefiningProp = (potentialObject, prop) =>
  potentialObject[prop] || potentialObject;

const defaultIcons = {
  close: <HardwareIcClose24px />
};

/**
 * @typedef Props
 * @prop {(event: React.SyntheticEvent) => void} [onChange]
 * @prop {(x: string) => void} [onSelect]
 * @prop {boolean} [disabled]
 * @prop {string} [id]
 * @prop {string} [value]
 * @prop {{close: React.ReactNode}} [icons]
 * @prop {string[] | number[] | Object[]} [items]
 * @prop {React.ReactNode} [template]
 * @prop {boolean} [hideClearButton]
 * @prop {string} [placeholder]
 * @prop {boolean} [open]
 * @prop {boolean} [required]
 * @prop {() => void} [onOpen]
 * @prop {() => void} [onClose]
 * @prop {() => void} [onClear]
 * @prop {() => void} [onFocus]
 * @prop {() => void} [onBlur]
 * @prop {() => void} [onEnter]
 * @prop {boolean} [onFocusShow]
 * @prop {string | number} [visibleOptions]
 * @prop {string} [filterProp]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifTypeahead = (props) => {
  const {
    icons: userIcons = {},
    id = '',
    items = [],
    value = '',
    onChange = null,
    onSelect = null,
    template = DefaultOptionTemplate,
    hideClearButton = false,
    placeholder = '',
    open = false,
    disabled = false,
    required = false,
    onOpen = () => null,
    onClose = () => null,
    onClear = () => null,
    onFocus = () => null,
    onBlur = () => null,
    onEnter = () => null,
    onFocusShow = false,
    visibleOptions = 5,
    filterProp = '',
    ...other
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [listOptions, setListOptions] = useState(items);
  const [toggle, setToggle] = useState(false);
  const [currentOption, setCurrentOption] = useState(-1);
  const [activeDescendant, setActiveDescendant] = useState('');
  const typeaheadRef = useRef();
  const typeaheadInputRef = useRef();
  const formFieldContext = useContext(FormFieldContext);
  const icons = {
    ...defaultIcons,
    ...userIcons
  };

  useEffect(() => {
    formFieldContext.setFormElementType('textInput');
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-input-${nextUniqueId++}`);
    formFieldContext.setActive(true);
    formFieldContext.setValue(value);
    formFieldContext.setRequired(required);
    formFieldContext.setDisabled(disabled);

    formFieldContext.setHasPlaceholder(
      placeholder && placeholder.length && typeof placeholder === 'string'
    );
    document.addEventListener('click', clickOutside);

    return () => {
      document.removeEventListener('click', clickOutside);
    };
  }, []);

  useEffect(() => {
    setCurrentOption(-1);
    setListOptions(filterOptions());
    formFieldContext.setValue(value);
    formFieldContext.setHasContent(value.length > 0);
    if (value.length !== 0) {
      onOpen();
    }
  }, [value]);

  useEffect(() => {
    if (currentOption === -1) {
      setActiveDescendant('');
    } else {
      setActiveDescendant(`${formFieldContext.id}-option-${currentOption}`);
      typeaheadInputRef.current.focus();
    }
  }, [currentOption]);

  useEffect(() => {
    setListOptions(items);
  }, [items]);

  const clickOutside = event => {
    if (!isOpen && canHandleOutside(typeaheadRef, event)) {
      setIsOpen(false);
    }
  };

  const filterOptions = () =>
    items.filter(option => {
      const item = returnDefiningProp(option, filterProp);

      return item.toLowerCase().includes(value.toLowerCase());
    });

  const handleOnBlur = event => {
    formFieldContext.toggleFocus(false);
    if (currentOption === -1) {
      onClose();
    }
    onBlur();
    if (onFocusShow) {
      setIsOpen(false);
    }
  };

  const handleOnFocus = () => {
    formFieldContext.toggleFocus(true);
    onFocus();
    if (onFocusShow) {
      setIsOpen(true);
    }
  };

  const handleOnSelect = selectedValue => {
    onSelect(selectedValue);
    formFieldContext.toggleFocus(false);
  };

  const handleOnChange = event => {
    onChange(event);
  };

  const handleOnClick = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      onClose();
    } else {
      onOpen();
      formFieldContext.toggleFocus(true);
    }
  };

  const handleOnClickRemoveValue = () => {
    onChange({ target: { value: '' } });
    onClear();
    setCurrentOption(-1);
    typeaheadInputRef.current.focus();
  };

  const handleKeyDown = event => {
    const pressedKey = event.key;

    switch (pressedKey) {
      case 'ArrowDown':
        event.preventDefault();
        if (currentOption < listOptions.length - 1) {
          setCurrentOption(currentOption + 1);
        } else {
          setCurrentOption(0);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (currentOption > 0) {
          setCurrentOption(currentOption - 1);
        } else {
          setCurrentOption(listOptions.length - 1);
        }
        break;

      case 'Enter':
        if (currentOption !== -1 && !listOptions[currentOption].disabled) {
          onSelect(returnDefiningProp(listOptions[currentOption], filterProp));
        }
        onEnter();
        formFieldContext.toggleFocus(false);
        break;

      default:
        break;
    }
  };

  const classes = classNames({
    'motif-typeahead': true,
    'motif-typeahead-open':
      open || (isOpen && value.length === 0) || (formFieldContext.isFocused && value.length > 0),
    'motif-input-component': true
  });

  const inputClasses = classNames({
    'motif-input': true,
    'motif-input-invalid': formFieldContext.invalid
  });

  return (
    <TypeaheadProvider
      value={{
        value,
        currentOption,
        id: formFieldContext.id,
        handleOnSelect,
        template,
        filterProp
      }}
    >
      <div ref={typeaheadRef} className={classes} data-motif-typeahead>
        <input
          ref={typeaheadInputRef}
          id={formFieldContext.id}
          className={inputClasses}
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          onClick={handleOnClick}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={
            open ||
            (isOpen && value.length === 0) ||
            (formFieldContext.isFocused && value.length > 0)
          }
          aria-controls={`${formFieldContext.id}-listbox`}
          aria-activedescendant={activeDescendant}
          {...other}
        />
        {value.length > 0 && !disabled && !hideClearButton && (
          <MotifIconButton
            className="motif-typeahead-close-button"
            onClick={handleOnClickRemoveValue}
            aria-label={`Remove ${value}`}
          >
            {icons.close}
          </MotifIconButton>
        )}

        {value.length > 0 && (
          <span className="motif-typeahead-screen-reader" aria-live="assertive">
            {listOptions.length === 1
              ? `${listOptions.length} result found`
              : `${listOptions.length} results found`}
          </span>
        )}

        {listOptions.length > 0 && (
          <ul
            className="motif-typeahead-list"
            id={`${formFieldContext.id}-listbox`}
            role="listbox"
            style={{ maxHeight: `${visibleOptions * OPTION_HEIGHT}rem` }}
          >
            {listOptions.map((item, index) => (
              <TypeaheadOption key={index} item={item} index={index} />
            ))}
          </ul>
        )}
      </div>
    </TypeaheadProvider>
  );
};

MotifTypeahead.propTypes = {
  icons: PropTypes.shape({
    close: PropTypes.node
  }),
  id: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
  ),
  value: PropTypes.string,
  template: PropTypes.func,
  hideClearButton: PropTypes.bool,
  placeholder: PropTypes.string,
  open: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onFocusShow: PropTypes.bool,
  visibleOptions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filterProp: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onClear: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onEnter: PropTypes.func
};

export { MotifTypeahead };
